// TDS2 SHIM — replace with @salesforce/trailhead-ui equivalents when TDS2 lands.
// Generic centered "system state" with an optional inline toast on top + a state
// card below. Mirrors the prototype's `.unit-ab-state-stack` /
// `.unit-ab-state-plain` layout (ChatComponentsPage.tsx + app.css 851-1075).
// All content is supplied via slot props so callers can render any kind of
// error (connection lost, gateway 4xx/5xx, compile/deploy, etc.) on the same
// chrome. Pass toastHeading/toastMessage to render the shared tds-inline-toast.
import React from 'react';
import PropTypes from 'prop-types';
import BetaTag from 'components/agentforce/BetaTag';

const VARIANTS = ['default', 'danger'];

export default function SystemErrorState({
  variant = 'default',
  toast,
  toastHeading,
  toastMessage,
  toastVariant = 'error',
  mascotUrl,
  icon,
  eyebrow,
  title,
  body,
  details,
  actions,
  ariaLive = 'polite',
}) {
  const cardClasses = ['tds2-agentforce-state-card'];

  if (variant === 'danger') {
    cardClasses.push('tds2-agentforce-state-card--danger');
  }

  // A caller can pass a fully-formed `toast` node, or just the copy via
  // toastHeading/toastMessage and let the shared tds-inline-toast render here so
  // every error state gets a consistent inline toast above the card.
  const toastNode = toast || (toastHeading || toastMessage ? (
    <tds-inline-toast variant={toastVariant} heading={toastHeading} message={toastMessage}>
    </tds-inline-toast>
  ) : null);

  return (
    <div
      className="tds2-agentforce-state-stack"
      data-tds2-shim="SystemErrorState"
    >
      {toastNode}
      <div
        className={cardClasses.join(' ')}
        role="status"
        aria-live={ariaLive}
      >
        {mascotUrl ? (
          <img
            src={mascotUrl}
            alt=""
            width="120"
            className="tds2-agentforce-welcome__mascot"
            aria-hidden="true"
          />
        ) : icon ? (
          <div className="tds2-agentforce-state-card__icon" aria-hidden="true">
            {icon}
          </div>
        ) : null}
        <BetaTag />
        {eyebrow ? (
          <div className="tds2-agentforce-state-card__eyebrow">{eyebrow}</div>
        ) : null}
        {title ? (
          <h2 className="tds2-agentforce-state-card__title">{title}</h2>
        ) : null}
        {body ? (
          <div className="tds2-agentforce-state-card__body">{body}</div>
        ) : null}
        {details ? (
          <div className="tds2-agentforce-state-card__details">{details}</div>
        ) : null}
        {actions ? (
          <div className="tds2-agentforce-state-card__actions">{actions}</div>
        ) : null}
      </div>
    </div>
  );
}

SystemErrorState.propTypes = {
  variant: PropTypes.oneOf(VARIANTS),
  toast: PropTypes.node,
  toastHeading: PropTypes.string,
  toastMessage: PropTypes.string,
  toastVariant: PropTypes.string,
  mascotUrl: PropTypes.string,
  icon: PropTypes.node,
  eyebrow: PropTypes.node,
  title: PropTypes.node,
  body: PropTypes.node,
  details: PropTypes.node,
  actions: PropTypes.node,
  ariaLive: PropTypes.oneOf(['off', 'polite', 'assertive']),
};
